import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { KROK, MAX_KROKU, UI, VSTUP, EASE_OUT, useBezPohybu } from "@/lib/pohyb";
import { AlertTriangle, Shield, TrendingUp, ShieldCheck, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * LeakageRadar — list of "missing protection" red flags + a coverage score.
 *
 * Fetches /api/leakage/{contact_id} (defined in routes/client_folder.py).
 * v4.27 — jasné rozlišení stavů panelu (Fáze 4B / WS-C):
 *   • loading  → načítá se
 *   • error    → načtení selhalo → chyba + retry (NIKDY se netváří jako prázdno / skóre 0)
 *   • unknown  → coverage_score není číslo → skóre „—" (ne „0")
 *   • real 0   → coverage_score === 0 → skóre „0"
 *   • empty    → data načtena, ale žádné signály → kompaktní srozumitelný empty
 *   • data     → karty signálů
 */

// Severity uses semantic tokens — these are intentional "danger / warn / info" colors
// independent of brand theme switch (still flag visibility no matter the palette).
const SEVERITY_STYLE = {
  /* `text` = drobný text štítku (tmavší odstín, ≥ 4,5:1), `color` = proužek, rámeček, ikona. */
  critical: { color: "var(--negative)", text: "var(--negative-text)", label: "KRITICKÉ" },
  high:     { color: "var(--warn)",   text: "var(--warn-text)",     label: "Vysoké"   },
  medium:   { color: "var(--warn)",   text: "var(--warn-text)",     label: "Střední"  },
  low:      { color: "var(--info)",   text: "var(--info-text)",     label: "Nízké"    },
};

export default function LeakageRadar({ contactId }) {
  const bezPohybu = useBezPohybu();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Request token — poslední požadavek vyhrává (ochrana proti závodu při změně
  // contactId i při retry). Nezaměňovat chybu jednoho běhu za výsledek jiného.
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/api/leakage/${contactId}`);
      if (token === reqRef.current) { setData(res.data); setError(false); }
    } catch {
      // Chyba NENÍ prázdno — nefabrikuj skóre 0 ani „vše pokryto".
      if (token === reqRef.current) setError(true);
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="py-12 text-center text-xs" style={{ color: "var(--muted)" }} data-testid="leakage-radar-loading">
        Analyzuji portfolio…
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4" data-testid="leakage-radar-error">
        <div className="rounded-2xl p-6 text-center" style={{ background: "var(--surface)", border: "1px solid var(--negative)" }}>
          <AlertTriangle size={22} className="mx-auto mb-2" style={{ color: "var(--negative)" }} />
          <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>Radar se nepodařilo načíst</div>
          <div className="text-drobny mt-1 leading-relaxed" style={{ color: "var(--muted)" }}>
            Data nejsou k dispozici — nejde o prázdné portfolio. Zkuste to prosím znovu.
          </div>
          <button
            onClick={load}
            data-testid="leakage-radar-retry"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
            style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
          >
            <RefreshCw size={12} /> Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  // ── Data načtena ──────────────────────────────────────────────────────────
  const rawScore = data?.coverage_score;
  // Skutečné číslo (včetně 0) vs. neznámo (null/undefined/NaN) → „—".
  const hasScore = typeof rawScore === "number" && Number.isFinite(rawScore);
  const score = hasScore ? Math.max(0, Math.min(100, rawScore)) : null;
  const signals = Array.isArray(data?.signals) ? data.signals : [];

  const scoreColor = !hasScore
    ? "var(--muted)"
    : score >= 80 ? "var(--positive)" : score >= 50 ? "var(--warn)" : "var(--negative)"; // střed = pozor, ne --accent (v Lime zelená = úspěch, CRM-15)
  const gaugeFrac = hasScore ? score / 100 : 0;
  const gaugeCenter = !hasScore ? "—" : (score >= 80 ? "✓" : `${100 - score}%`);
  const criticalCount = Number.isFinite(data?.critical_count) ? data.critical_count : 0;

  const subtitle = signals.length === 0
    ? (hasScore ? "Portfolio kompletní — žádné chybějící produkty" : "Zatím žádné signály k zobrazení")
    : `${signals.length} ${signals.length === 1 ? "příležitost" : signals.length <= 4 ? "příležitosti" : "příležitostí"}` +
      (criticalCount ? ` · ${criticalCount} kritických` : "");

  return (
    <div className="space-y-4" data-testid="leakage-radar">
      {/* Coverage score header card */}
      <motion.div
        initial={bezPohybu ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        transition={bezPohybu ? { duration: 0 } : { duration: UI, ease: EASE_OUT }}
        className="relative rounded-2xl p-5 overflow-hidden"
        style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-px"
             style={{ background: `linear-gradient(90deg, transparent, ${scoreColor} 50%, transparent)` }} />
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-popisek uppercase tracking-[0.22em] font-semibold flex items-center gap-1.5" style={{ color: "var(--accent)" }}>
              <Shield size={11} /> Coverage Score
            </div>
            <div className="text-3xl mt-1 tabular-nums" style={{ fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--ink)" }}>
              <span data-testid="leakage-radar-score" style={{ color: scoreColor }}>{hasScore ? score : "—"}</span> <span className="text-base" style={{ color: "var(--muted)" }}>/ 100</span>
            </div>
            <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>{subtitle}</div>
          </div>
          {/* Mini gauge */}
          <div className="relative w-20 h-20 flex-shrink-0">
            <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
              <circle cx="40" cy="40" r="32" fill="none" stroke="var(--line)" strokeWidth="6" />
              <motion.circle
                cx="40" cy="40" r="32" fill="none"
                stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 32}
                initial={bezPohybu ? false : { strokeDashoffset: 2 * Math.PI * 32 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 32 * (1 - gaugeFrac) }}
                transition={bezPohybu ? { duration: 0 } : { duration: VSTUP, ease: EASE_OUT }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-drobny" style={{ color: "var(--muted)" }}>
              {gaugeCenter}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Signals / empty */}
      {signals.length === 0 ? (
        <div
          className="rounded-xl p-5 text-center"
          data-testid="leakage-radar-empty"
          style={hasScore
            ? { background: "var(--positive-soft)", border: "1px solid color-mix(in srgb, var(--positive) 30%, transparent)" }
            : { background: "var(--surface)", border: "1px solid var(--line)" }}
        >
          {hasScore ? (
            <ShieldCheck size={18} className="mx-auto mb-1.5" style={{ color: "var(--positive)" }} />
          ) : (
            <Shield size={18} className="mx-auto mb-1.5" style={{ color: "var(--muted)" }} />
          )}
          <div className="text-sm" style={{ color: "var(--ink)" }}>
            {hasScore ? "Vše pokryto." : "Žádné signály"}
          </div>
          <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>
            {hasScore
              ? "Žádné chybějící klíčové produkty nebyly detekovány."
              : "Zatím nejsou k dispozici žádné signály pro tohoto klienta."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {signals.map((s, i) => (
            <LeakageCard key={s?.id ?? i} signal={s || {}} idx={i} delay={Math.min(i, MAX_KROKU) * KROK} bezPohybu={bezPohybu} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeakageCard({ signal, idx, delay, bezPohybu }) {
  const sev = SEVERITY_STYLE[signal.severity] || SEVERITY_STYLE.low;
  return (
    <motion.div
      initial={bezPohybu ? false : { opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={bezPohybu ? { duration: 0 } : { delay, duration: UI, ease: EASE_OUT }}
      className="relative rounded-xl p-3.5 pl-5 overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
      data-testid={`leakage-signal-${signal.id ?? idx}`}
    >
      <div className="absolute top-0 left-0 bottom-0 w-[3px]" style={{ background: sev.color }} />
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: "var(--accent-soft)", border: `1px solid ${sev.color}` }}>
          {signal.severity === "critical" ? (
            <AlertTriangle size={14} style={{ color: sev.color }} />
          ) : (
            <TrendingUp size={14} style={{ color: sev.color }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{signal.label}</span>
            <span className="text-popisek uppercase tracking-[0.15em] px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: "var(--accent-soft)", color: sev.text, border: `1px solid ${sev.color}` }}>
              {sev.label}
            </span>
          </div>
          {signal.reason && <div className="text-drobny mt-1 leading-relaxed" style={{ color: "var(--muted)" }}>{signal.reason}</div>}
          {signal.suggested_action && (
            <div className="text-drobny mt-1.5 italic" style={{ color: "var(--accent)" }}>
              → {signal.suggested_action}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
